import MDBadge from "#components/MDBadge/index.js";
import {
  ArrowDropDown,
  CheckCircle,
  CheckCircleOutline,
  FilterList,
  Close,
  OpenInNew,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Link,
  Menu,
  MenuItem,
  Pagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import BasicDialog from "components/BasicDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { usePermissionMonitor } from "components/PermissionMonitor";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import dayjs from "dayjs";
import DataTable from "examples/Tables/DataTable";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import branchesService from "services/branches-service";
import departmentsService from "services/departments-service";
import tasksService from "services/tasks-service";
import usersService from "services/users-service";
import useOpen from "utils/useOpen";
import Attachments from "./Attachments";
import KpiAttachments from "#components/Attachments/index.js";
import FilterTasksDialog from "./FiltersDialog";
import TasksEditNewDialog from "./TasksEditNewDialog";

const TASK_STATUS_COLORS = {
  [1]: "warning",
  [2]: "info",
  [3]: "success",
  [4]: "secondary",
};

const TASK_PRIORITY_COLORS = {
  [1]: "secondary",
  [2]: "warning",
  [3]: "error",
};

export default function AllTasks({ permissions }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState(null);
  const [count, setCount] = useState(0);
  const [query, setQuery] = useState({});
  const [options, setOptions] = useState({
    priorities: null,
    users: null,
    statuses: null,
    departments: null,
    branches: null,
  });
  const [searchSignal, setSearchSignal] = useState(false);
  const open = useOpen({
    filters: false,
    create: false,
    edit: false,
    alert: false,
  });
  const { label } = useLanguage();

  const columns = [
    {
      Header: label("title-singular"),
      accessor: "title",
      width: "100%",

      bodyCellStyle: {
        whiteSpace: "unset",
        overflow: "unset",
        textOverflow: "unset",
      },
    },
    {
      Header: label("duedate-singular"),
      accessor: "dueDate",
    },
    {
      Header: label("status-singular"),
      accessor: "status",
    },
    {
      Header: label("priority-singular"),
      accessor: "priority",
    },
    {
      Header: label("assigned-to-singular"),
      accessor: "assignedTo",
    },
    // {
    //   Header: label("branch-singular"),
    //   accessor: "branch",
    // },
    // {
    //   Header: label("date-singular"),
    //   accessor: "auditDate",
    // },
  ];

  if (permissions.update || permissions.delete) {
    columns.push({
      Header: "",
      accessor: "actions",
      align: "right",
    });
  }

  async function getOptions() {
    const [
      prioritiesResponse,
      usersResponse,
      statusesResponse,
      departmentsResponse,
      branchesResponse,
    ] = await Promise.all([
      tasksService.getTaskPriorities(),
      usersService.getUsers({ tasks: JSON.stringify(query), view: "minimal" }),
      tasksService.getTaskStatuses(),
      departmentsService.getDepartments(),
      branchesService.getBranches({ tasks: JSON.stringify(query) }),
    ]);

    // setOptions((options) => ({
    //   ...options,
    //   users: usersResponse[0].ok
    //     ? usersResponse[1].map((item) => {
    //         return {
    //           id: item.id,
    //           label: item.name,
    //         };
    //       })
    //     : [],
    // }));

    setOptions((options) => ({
      ...options,
      priorities: prioritiesResponse[0].ok
        ? prioritiesResponse[1].map((item) => {
            return {
              id: item.id,
              label: item.name,
            };
          })
        : [],
    }));

    setOptions((options) => ({
      ...options,
      statuses: statusesResponse[0].ok
        ? statusesResponse[1].map((item) => {
            return {
              id: item.id,
              label: item.name,
            };
          })
        : [],
    }));

    setOptions((options) => ({
      ...options,
      departments: departmentsResponse[0].ok
        ? departmentsResponse[1].map((item) => {
            return {
              id: item.id,
              label: item.description,
            };
          })
        : [],
    }));

    // if (branchesResponse[0].ok) {
    //   const branchGroups = branchesResponse[1];
    //   const branches = branchGroups
    //     .map((branchGroup) => {
    //       return branchGroup.branches.map((branch) => {
    //         return {
    //           id: branch.id,
    //           label: branch.name,
    //           groupId: branchGroup.id,
    //           groupLabel: branchGroup.name,
    //         };
    //       });
    //     })
    //     .flat();
    //   setOptions((options) => ({
    //     ...options,
    //     branches: branches,
    //   }));
    // } else {
    //   setOptions((options) => ({
    //     ...options,
    //     branches: [],
    //   }));
    // }
  }

  const filters = [
    {
      key: "date",
      label: label("duedate-singular"),
      type: "date",
    },
    {
      key: "priorities",
      label: label("priority-plural"),
      options: options.priorities || [],
    },
    {
      key: "users",
      label: label("user-plural"),
      options: options.users || [],
    },
    {
      key: "statuses",
      label: label("status-plural"),
      options: options.statuses || [],
    },
    {
      key: "departments",
      label: label("department-plural"),
      options: options.departments || [],
    },
    {
      key: "branches",
      label: label("branch-plural"),
      options: options.branches || [],
    },
    {
      key: "audits",
      label: label("audit-plural"),
      options: options.audits || [],
      // getOptions: async (branchIds) => {
      //   const [response, data] = await auditsService.getAudits({
      //     branchId: branchIds.join(","),
      //     tasks: JSON.stringify(query),
      //   });
      //   return data.map((audit) => ({
      //     id: audit.id,
      //     label: audit.description,
      //   }));
      // },
      // dependsOn: "branches",
    },
  ];

  useEffect(() => {
    getOptions();
  }, []);

  async function handleAddTask(newElement) {
    const [response, data] = await tasksService.createTask({}, newElement);
    if (response.ok) {
      const newQuery = {};
      filters.map((filter) => {
        const value = searchParams.get(filter.key);
        if (value) {
          newQuery[filter.key] = value;
        }
      });
      setSearchSignal(newQuery);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateTask(newElement) {
    const [response, data] = await tasksService.updateTask(
      newElement.id,
      newElement,
    );
    if (response.ok) {
      const newQuery = {};
      filters.map((filter) => {
        const value = searchParams.get(filter.key);
        if (value) {
          newQuery[filter.key] = value;
        }
      });
      setSearchSignal(newQuery);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteTask(id) {
    const [response, data] = await tasksService.removeTask(id);
    if (response.ok) {
      const newQuery = {};
      filters.map((filter) => {
        const value = searchParams.get(filter.key);
        if (value) {
          newQuery[filter.key] = value;
        }
      });
      setSearchSignal(newQuery);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function getTasks(internalQuery, callback) {
    if (internalQuery) {
      setQuery(internalQuery);
    }

    const requestQuery = internalQuery || query;

    const response = await tasksService.getTasks({
      ...requestQuery,
      includeFilters: true,
    });
    const [ok, data] = response;
    setTasks(data.data);
    setCount(data.count);

    setOptions((options) => ({
      ...options,
      users:
        data.filters?.users?.map((item) => {
          return {
            id: item.id,
            label: item.name,
          };
        }) || [],
      branches:
        data.filters?.branches?.map((branch) => ({
          id: branch.id,
          label: branch.name,
          groupId: branch.branchGroup.id,
          groupLabel: branch.branchGroup.name,
        })) || [],
      audits:
        data.filters?.audits?.map((audit) => ({
          id: audit.id,
          label: audit.description,
        })) || [],
    }));

    if (callback) {
      await callback?.(response);
    }
    return response;
  }

  const rows = useMemo(
    () =>
      tasks?.length > 0
        ? tasks.map((task) => {
            return {
              data: task,
              title: (
                <MDTypography variant="inherit">
                  {!task.auditKpi ? (
                    <MDBadge badgeContent={`STANDALONE`} color="primary" />
                  ) : (
                    (task.referenceCode || task.auditKpi.kpi.referenceCode) +
                    " —"
                  )}{" "}
                  {[task.title].filter((v) => v).join(" - ")}
                </MDTypography>
              ),

              dueDate: (
                <MDTypography variant="inherit">
                  {
                    moment(new Date(task.dueDate))
                      .format("MMM Do YYYY,h:mm a")
                      .split(",")[0]
                  }
                  <br />
                  {
                    moment(new Date(task.dueDate))
                      .format("MMM Do YYYY,h:mm a")
                      .split(",")[1]
                  }
                </MDTypography>
              ),

              status: (
                <Chip
                  label={task.status?.name}
                  sx={{ color: "white !important" }}
                  size="small"
                  color={TASK_STATUS_COLORS[task.statusId || 0]}
                />
              ),
              priority: (
                <Chip
                  label={task.priority?.name}
                  sx={{ color: "white !important" }}
                  size="small"
                  color={TASK_PRIORITY_COLORS[task.priorityId || 0]}
                />
              ),
              assignedTo: (
                <MDTypography
                  variant="inherit"
                  color={task.assignedTo?.name ? null : "GrayText"}
                  fontStyle={task.assignedTo?.name ? null : "italic"}
                >
                  {task.assignedTo?.name || "None"}
                </MDTypography>
              ),
              branch: (
                <MDTypography
                  variant="inherit"
                  color={task?.auditKpi?.audit?.branch ? null : "GrayText"}
                  fontStyle={task?.auditKpi?.audit?.branch ? null : "italic"}
                >
                  {
                    <>
                      {task?.auditKpi?.audit?.branch?.branchGroup?.name}
                      <br />
                      {task?.auditKpi?.audit?.branch?.name}
                    </>
                  }
                </MDTypography>
              ),

              auditDate: (
                <MDTypography variant="inherit">
                  {task.auditKpi?.audit?.date ? (
                    <>
                      {
                        moment(
                          new Date(task.auditKpi.audit?.date)
                            .toISOString()
                            .replace("z", ""),
                        )
                          .format("MMM Do YYYY,h:mm a")
                          .split(",")[0]
                      }
                      <br />
                      {
                        moment(
                          new Date(task.auditKpi.audit?.date)
                            .toISOString()
                            .replace("z", ""),
                        )
                          .format("MMM Do YYYY,h:mm a")
                          .split(",")[1]
                      }
                    </>
                  ) : (
                    ""
                  )}
                </MDTypography>
              ),

              actions: (
                <MDBox
                  display="flex"
                  flexDirection="row"
                  justifyContent="end"
                  alignItems="center"
                >
                  {permissions.update && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        open.change("edit", {
                          open: Math.random(),
                          data: task,
                        });
                      }}
                    >
                      <Icon>edit</Icon>
                    </IconButton>
                  )}
                  {permissions.delete && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        open.change("alert", {
                          open: Math.random(),
                          data: task,
                        });
                      }}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  )}
                </MDBox>
              ),
            };
          })
        : [],
    [tasks],
  );

  // if (!options.priorities || !options.statuses || !options.users) {
  // return "";
  // }

  return (
    <>
      <AlertDialog
        open={open.get("alert")?.open}
        setOpen={(v) => {
          open.change("alert", v);
        }}
        title={label("delete-alert-title")}
        content={label("delete-alert-message")}
        onOk={async () => {
          await handleDeleteTask(open.get("alert")?.data?.id);
        }}
      />
      <TasksEditNewDialog
        isOpen={open.get("create")?.open}
        onSubmit={handleAddTask}
      />
      <TasksEditNewDialog
        type="edit"
        isOpen={open.get("edit")?.open}
        onSubmit={handleUpdateTask}
        data={open.get("edit")?.data}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                gap={1}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("task-plural")}{" "}
                  {permissions.get ||
                  permissions.getMy ||
                  permissions.getMyUserGroup
                    ? `(${count || 0})`
                    : ""}
                </MDTypography>
                <Box sx={{ flex: 1 }} />
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      open.change("create", { open: Math.random() });
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("add-new-task-button")}
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ||
              permissions.getMy ||
              permissions.getMyUserGroup ? (
                <>
                  <MDBox
                    p={2}
                    mt={2}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      gap: 1,
                    }}
                  >
                    <FilterTasksDialog
                      onChange={() => {
                        const newQuery = {};
                        filters.map((filter) => {
                          const value = searchParams.get(filter.key);
                          if (value) {
                            newQuery[filter.key] = value;
                          }
                        });
                        setSearchSignal(newQuery);
                      }}
                      filters={filters}
                      open={open.get("filters")}
                      handleClose={() => {
                        open.change("filters", false);
                      }}
                    />
                    <Badge
                      badgeContent={
                        Object.keys(query).length - 3 < 0
                          ? 0
                          : Object.keys(query).length - 3
                      }
                      color="primary"
                    >
                      <IconButton
                        onClick={() => {
                          open.change("filters", true);
                        }}
                      >
                        <FilterList />
                      </IconButton>
                    </Badge>
                    <Box sx={{ flex: 1 }}>
                      <TableSearch
                        id="all-tasks"
                        internalLoading={!Boolean(tasks)}
                        fullWidth
                        setData={setTasks}
                        refreshSignal={searchSignal}
                        refreshSignalToQuery={(v) => v}
                        onSearch={async (request, callback) => {
                          const newQuery = {
                            ...request,
                            skip:
                              (Number(searchParams.get("page") || 1) - 1) * 10,
                            take: 10,
                          };
                          filters.map((filter) => {
                            const value = searchParams.get(filter.key);
                            if (value) {
                              newQuery[filter.key] = value;
                            }
                            if (searchParams.get("from"))
                              newQuery.dateFrom = searchParams.get("from");
                            if (searchParams.get("to"))
                              newQuery.dateTo = searchParams.get("to");
                            if (Number(searchParams.get("id")))
                              newQuery.id = Number(searchParams.get("id"));
                          });

                          setQuery(newQuery);
                          callback(await getTasks(newQuery));
                        }}
                        label={label("search-tasks-label")}
                      />
                    </Box>
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(tasks)}
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                      includeDetails={(row) => {
                        return (
                          <TaskDetails
                            task={{
                              ...row.original.data,
                              suggestedDueDate: row.original.data
                                ?.suggestedDueDate
                                ? dayjs(row.original.data.suggestedDueDate)
                                : undefined,
                            }}
                            getTasks={getTasks}
                            options={options}
                          />
                        );
                      }}
                    />
                    <MDBox sx={{ padding: 2 }}>
                      <Pagination
                        count={Math.floor(count / 10) + 1}
                        color={"info"}
                        variant="outlined"
                        page={Number(searchParams.get("page"))}
                        onChange={(_, newPage) => {
                          setSearchParams((curr) => {
                            curr.set("page", newPage);
                            return curr;
                          });

                          getTasks({
                            ...query,
                            skip: (Number(newPage || 1) - 1) * 10,
                          });
                        }}
                      />
                    </MDBox>
                    {/* {mode == "table" ? (
                      
                    ) : (
                      <Box sx={{ px: 2, pb: 2 }}>
                        <Grid container spacing={1}>
                          {tasks.map((task) => {
                            return (
                              <TaskElement
                                options={options}
                                setEditDialogIsOpen={setEditDialogIsOpen}
                                setCurrentTask={setCurrentTask}
                                task={task || {}}
                                key={"TaskG: " + task?.id}
                                setTasks={setTasks}
                              />
                            );
                          })}
                        </Grid>
                      </Box>
                    )} */}
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}

function ContainedTextField({ value, onChange, ...props }) {
  const [internalValue, setInternalValue] = useState(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  return (
    <TextField
      {...props}
      value={internalValue}
      onChange={(e) => {
        setInternalValue(e.target.value);
        onChange?.(e);
      }}
    />
  );
}

function TaskDetails({ task: initialTask, getTasks, options }) {
  const { label } = useLanguage();
  const [updatedTask, setUpdatedTask] = useState({});
  const [open, setOpen] = useState({});
  const task = { ...initialTask, ...updatedTask };
  const [loading, setLoading] = useState(false);
  const can = usePermissionMonitor();
  const hasChanges = Object.keys(updatedTask).length > 0;

  async function approveSuggestedDueDate(taskId) {
    const promise = tasksService.approveSuggestedDueDate(taskId);
    toast.promise(promise, {
      position: "bottom-right",
      loading: label("loading"),
      success: label("saved"),
      error: label("not-saved"),
      duration: 5,
    });
    const [response, data] = await promise;
    if (response.ok) {
      await getTasks(null, () => {
        const newUpdatedTask = { ...updatedTask };
        delete newUpdatedTask.suggestDueDate;
        setUpdatedTask(newUpdatedTask);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }

  async function denySuggestedDueDate(taskId) {
    const promise = tasksService.denySuggestedDueDate(taskId);
    toast.promise(promise, {
      position: "bottom-right",
      loading: label("loading"),
      success: label("saved"),
      error: label("not-saved"),
      duration: 5,
    });
    const [response, data] = await promise;
    if (response.ok) {
      await getTasks(null, () => {
        const newUpdatedTask = { ...updatedTask };
        delete newUpdatedTask.suggestDueDate;
        setUpdatedTask(newUpdatedTask);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }

  async function saveChanges() {
    if (!initialTask?.id || !hasChanges) return;

    setLoading(true);
    const promise = (async () => {
      const [response, data] = await tasksService.answerTask(initialTask.id, {
        ...updatedTask,
        suggestedDueDate: updatedTask.suggestedDueDate?.$d || undefined,
      });

      if (!response.ok) {
        throw new Error(data?.error || label("not-saved"));
      }

      return [response, data];
    })();

    toast.promise(promise, {
      position: "bottom-right",
      loading: label("loading"),
      success: label("saved"),
      error: label("not-saved"),
      duration: 1,
    });

    try {
      await promise;
      await getTasks(null, () => {
        setUpdatedTask({});
        setLoading(false);
      });
    } catch {
      setLoading(false);
    }
  }

  if (!task) return "Loading...";

  return (
    <Box
      sx={() => ({
        width: "100%",
        mb: -1.5,
      })}
    >
      {Boolean(open.attachment) ? (
        <BasicDialog
          title={label("attachment-singular")}
          content={
            <img
              style={{
                width: "100%",
              }}
              src={open?.attachment?.url}
            />
          }
          cancelButton={true}
          open={Boolean(open.attachment)}
          setOpen={(v) => {
            setOpen({ ...open, attachment: !v ? false : {} });
          }}
        />
      ) : null}
      <Grid
        container
        sx={{
          width: "100%",
          height: "100% !important",
          display: "flex",
        }}
      >
        <CardElement
          xs={12}
          noTopBorder
          noEndBorder
          title={
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <Typography
                fontSize="medium"
                fontWeight="500"
                sx={{ display: "flex", flexDirection: "row", gap: 1 }}
              >
                {initialTask?.auditKpi?.kpi
                  ? (task.referenceCode ||
                      initialTask?.auditKpi?.kpi.referenceCode) +
                    " - " +
                    task?.auditKpi?.audit?.branch?.branchGroup?.name +
                    " - " +
                    task?.auditKpi?.audit?.branch?.name
                  : ""}
                &nbsp;
                {initialTask?.auditKpi?.audit ? (
                  <Link
                    component={RouterLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    to={`/audits/${task?.auditKpi?.audit?.id}`}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Link
                      underline="always"
                      sx={{
                        fontSize: "16px",
                        fontWeight: "500",
                        cursor: "pointer",
                        textDecoration: "underline !important",
                      }}
                    >
                      View audit
                    </Link>
                    <OpenInNew />
                  </Link>
                ) : null}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Tooltip
                  title={
                    loading
                      ? label("loading")
                      : hasChanges
                        ? "Unsaved changes"
                        : "Up to date"
                  }
                >
                  {loading ? (
                    <CircularProgress size="18px" color="secondary" />
                  ) : hasChanges ? (
                    <CheckCircleOutline
                      color="warning"
                      sx={{ fontSize: "18px" }}
                    />
                  ) : (
                    <CheckCircle color="success" sx={{ fontSize: "18px" }} />
                  )}
                </Tooltip>
                {can("answer", "task") ? (
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    disabled={!hasChanges || loading}
                    onClick={saveChanges}
                    sx={{
                      py: 0.5,
                      px: 1,
                      minHeight: "0px",
                    }}
                  >
                    {label("save-button")}
                  </MDButton>
                ) : null}
                {can("answer", "task") ? (
                  <Tooltip title={label("cancel-button")}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!hasChanges || loading}
                        onClick={() => setUpdatedTask({})}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
                <Chip
                  label={task.priority.name}
                  sx={{ color: "white !important" }}
                  size="small"
                  color={
                    TASK_PRIORITY_COLORS[
                      updatedTask.priorityId || task.priority?.id || 0
                    ]
                  }
                />
                <Chip
                  label={
                    options.statuses?.find(
                      (status) => status.id == task.statusId,
                    )?.label
                  }
                  color={TASK_STATUS_COLORS[task.statusId || 0]}
                  size="small"
                  onDelete={
                    can("answer", "task")
                      ? (e) => {
                          setOpen({
                            ...open,
                            statusMenu: {
                              anchorElement: e.currentTarget,
                              open: true,
                            },
                          });
                        }
                      : null
                  }
                  sx={{ color: "white !important" }}
                  onClick={
                    can("answer", "task")
                      ? (e) => {
                          setOpen({
                            ...open,
                            statusMenu: {
                              anchorElement: e.currentTarget,
                              open: true,
                            },
                          });
                        }
                      : null
                  }
                  deleteIcon={
                    can("answer", "task") ? (
                      <ArrowDropDown sx={{ color: "white !important" }} />
                    ) : null
                  }
                  clickable={can("answer", "task")}
                />
                {can("answer", "task") ? (
                  <Menu
                    open={!!open.statusMenu?.open}
                    anchorEl={open.statusMenu?.anchorElement}
                    onClose={() => {
                      setOpen({
                        ...open,
                        statusMenu: {
                          ...open.statusMenu,
                          open: false,
                        },
                      });
                    }}
                  >
                    {options.statuses.map((status) => {
                      let disabled = false,
                        reason = "";

                      if (status.id >= 3) {
                        if (task.imageRequired && !task.attachments?.length) {
                          disabled = true;
                          reason =
                            "Requires at least 1 image before completion";
                        }
                      }
                      return (
                        <Tooltip title={disabled ? reason : ""}>
                          {" "}
                          <MenuItem
                            disabled={disabled}
                            onClick={() => {
                              setOpen({
                                ...open,
                                statusMenu: {
                                  ...open.statusMenu,
                                  open: false,
                                },
                              });
                              setUpdatedTask({
                                ...updatedTask,
                                statusId: status.id,
                              });
                            }}
                            selected={task.statusId == status.id}
                            key={"Status: " + status.id}
                            sx={{ borderRadius: "6px", px: 1.5, py: 0.75 }}
                          >
                            {status.label}
                          </MenuItem>
                        </Tooltip>
                      );
                    })}
                  </Menu>
                ) : null}
              </Box>
            </Box>
          }
          content={
            initialTask?.auditKpi?.kpi ? (
              <Grid container>
                <Grid
                  item
                  xs={
                    (task.inputAttachments?.length
                      ? task.inputAttachments
                      : task?.auditKpi?.attachments
                    )?.length
                      ? 8
                      : 12
                  }
                >
                  <Typography fontSize="medium">
                    <strong>{label("description-singular")}</strong>:{" "}
                    {task?.auditKpi?.description
                      ?.trim()
                      ?.split("\n")
                      .map((v) => (
                        <>
                          {v}
                          <br />
                        </>
                      ))}
                  </Typography>
                  <Typography fontSize="medium">
                    <strong>{label("deviation-singular")}</strong>:{" "}
                    {task?.auditKpi?.kpiDeviation?.description
                      ?.trim()
                      ?.split("\n")
                      .map((v) => (
                        <>
                          {v}
                          <br />
                        </>
                      ))}
                  </Typography>
                  <Typography fontSize="medium">
                    <strong>{label("risk-singular")}</strong>:{" "}
                    {task?.auditKpi?.kpiRisk?.description
                      ?.trim()
                      ?.split("\n")
                      .map((v) => (
                        <>
                          {v}
                          <br />
                        </>
                      ))}
                  </Typography>
                  <Typography fontSize="medium">
                    <strong>{label("action-singular")}</strong>:{" "}
                    {task?.auditKpi?.kpiAction?.description
                      ?.trim()
                      ?.split("\n")
                      .map((v) => (
                        <>
                          {v}
                          <br />
                        </>
                      ))}
                  </Typography>
                  {task?.auditKpi?.remarks?.trim() ? (
                    <Typography fontSize="medium">
                      <strong>{label("remark-plural")}</strong>:{" "}
                      {task?.auditKpi?.remarks
                        ?.trim()
                        ?.split("\n")
                        .map((v) => (
                          <>
                            {v}
                            <br />
                          </>
                        ))}
                    </Typography>
                  ) : (
                    ""
                  )}
                </Grid>
                {(task.inputAttachments?.length
                  ? task.inputAttachments
                  : task?.auditKpi?.attachments
                )?.length ? (
                  <Grid item xs={4}>
                    <Typography fontSize="medium">
                      <strong>{label("attachment-plural")}</strong>
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <KpiAttachments
                        attachments={
                          task.inputAttachments?.length
                            ? task.inputAttachments
                            : task?.auditKpi?.attachments
                        }
                        readOnly
                      />
                      {/* {(task.inputAttachments?.length
                        ? task.inputAttachments
                        : task?.auditKpi?.attachments
                      )?.map((attachment) => {
                        return (
                          <Card
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              gap: 1,
                            }}
                            variant="outlined"
                          >
                            <img
                              onClick={() => {
                                setOpen({ ...open, attachment });
                              }}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center",
                                maxHeight: "80px",
                                maxWidth: "80px",
                                cursor: "pointer",
                                borderRadius: "9px",
                              }}
                              src={attachment.url}
                              key={"Attachment: " + attachment.id}
                            />
                            {attachment.remarks ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: "400",
                                  lineHeight: "18px",
                                  fontSize: "15px",
                                  py: 1,
                                }}
                                color="GrayText"
                              >
                                {attachment.remarks}
                              </Typography>
                            ) : null}
                          </Card>
                        );
                      })} */}
                    </Box>
                  </Grid>
                ) : null}
              </Grid>
            ) : null
          }
        />

        <CardElement
          xs={4}
          title={label("description-singular")}
          content={
            <>
              <Typography fontSize="medium">
                {task?.actionToTake?.split("\n").map((v) => (
                  <>
                    {v}
                    <br />
                  </>
                ))}
              </Typography>
              <br />
              <Typography fontSize="medium" fontWeight="500">
                {label("additional-details")}
              </Typography>
              <Typography
                fontSize="medium"
                sx={(theme) => ({ color: "secondary" })}
              >
                {task?.actionDetails?.split("\n").map((v) => (
                  <>
                    {v}
                    <br />
                  </>
                ))}
              </Typography>
            </>
          }
        />

        <CardElement
          xs={4}
          title={label("remark-plural")}
          content={
            <ContainedTextField
              fullWidth
              multiline
              minRows={2}
              placeholder="Enter your remarks"
              value={task.remarks || ""}
              InputProps={{ readOnly: !can("answer", "task") }}
              onChange={
                can("answer", "task")
                  ? (e) => {
                      setUpdatedTask({
                        ...updatedTask,
                        remarks: e.target.value,
                      });
                    }
                  : null
              }
            />
          }
        />

        <CardElement
          xs={4}
          noEndBorder
          title={
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Typography fontSize="medium" fontWeight="500">
                {label("duedate-singular")}
              </Typography>
              <Tooltip
                title={
                  !can("answer", "task")
                    ? "You don't have permission to suggest due dates"
                    : !task.allowDueDateSuggestion
                      ? "A due date was already suggested"
                      : ""
                }
              >
                <Box>
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    sx={{
                      py: 0.5,
                      px: 1,
                      minHeight: "0px",
                    }}
                    onClick={(e) => {
                      setOpen({
                        ...open,
                        suggestDueDate: {
                          ...open.suggestDueDate,
                          open: !open.suggestDueDate?.open,
                          anchorEl: e.currentTarget,
                        },
                      });
                    }}
                    disabled={
                      !can("answer", "task") || !task.allowDueDateSuggestion
                    }
                  >
                    {label("suggest-duedate")}
                  </MDButton>
                </Box>
              </Tooltip>
            </Box>
          }
          content={
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  alignItems: "end",
                }}
              >
                <DateTimePicker
                  fullWidth
                  label={label("duedate-singular")}
                  sx={{ width: "100%" }}
                  value={dayjs(task.dueDate)}
                  readOnly
                  format="DD/MM/YYYY hh:mm A"
                />
                {can("approveChanges", "task") && task.suggestedDueDate ? (
                  <Box>
                    <Typography fontSize="medium" color="error">
                      {label("new-duedate-suggested")}{" "}
                      {moment(
                        new Date(task.suggestedDueDate.$d)
                          .toISOString()
                          .replace("z", ""),
                      ).format("MMM Do YYYY, h:mm a")}
                      .{" "}
                      <MDButton
                        variant="gradient"
                        color="info"
                        size="small"
                        sx={{
                          py: 0.5,
                          px: 1,
                          minHeight: "0px",
                        }}
                        onClick={() => approveSuggestedDueDate(task.id)}
                      >
                        {label("approve-suggestion")}
                      </MDButton>
                      <MDButton
                        variant="gradient"
                        color="primary"
                        size="small"
                        sx={{
                          py: 0.5,
                          px: 1,
                          minHeight: "0px",
                        }}
                        onClick={() => denySuggestedDueDate(task.id)}
                      >
                        {label("deny-suggestion")}
                      </MDButton>
                    </Typography>
                  </Box>
                ) : null}
                {can("answer", "task") ? (
                  <Menu
                    onClose={() => {
                      setOpen({
                        ...open,
                        suggestDueDate: {
                          ...open?.suggestDueDate,
                          open: false,
                        },
                      });
                    }}
                    open={open?.suggestDueDate?.open}
                    anchorEl={open?.suggestDueDate?.anchorEl}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        alignItems: "end",
                        p: 1,
                      }}
                    >
                      <DateTimePicker
                        fullWidth
                        label={label("suggest-duedate")}
                        sx={{ width: "100%" }}
                        value={task.suggestedDueDate}
                        onAccept={(e) => {
                          setUpdatedTask({
                            ...updatedTask,
                            suggestedDueDate: dayjs(e.$d),
                          });
                          setOpen({
                            ...open,
                            suggestDueDate: {
                              ...open?.suggestDueDate,
                              open: false,
                            },
                          });
                        }}
                        format="DD/MM/YYYY hh:mm A"
                        disabled={!can("answer", "task")}
                      />
                      {/* <MDButton
                        variant="gradient"
                        color="info"
                        size="small"
                        disabled={!task.suggestedDueDate}
                      >
                        Suggest due date
                      </MDButton> */}
                    </Box>
                  </Menu>
                ) : null}
              </Box>
            </LocalizationProvider>
          }
        />

        <CardElement
          xs={12}
          noEndBorder
          title={label("attachment-plural") + (task.imageRequired ? " *" : "")}
          content={
            <>
              <Attachments
                readOnly={!can("answer", "task")}
                attachments={task.attachments || []}
                onRemoveAttachment={
                  task.imageRequired &&
                  task.statusId >= 3 &&
                  task?.attachments?.length <= 1
                    ? null
                    : (_, index) => {
                        const newAttachments = [...task.attachments];
                        newAttachments.splice(index, 1);
                        setUpdatedTask({
                          ...updatedTask,
                          attachments: newAttachments,
                        });
                      }
                }
                reason={{
                  disabledRemove: "Cannot delete, minimum 1 image is required",
                }}
                onAddAttachments={(files) => {
                  setUpdatedTask({
                    ...updatedTask,
                    attachments: [
                      ...task.attachments,
                      ...files.map((url) => ({ url })),
                    ],
                  });
                }}
              />
            </>
          }
        />
      </Grid>
    </Box>
  );
}

function CardElement({ title, content, xs, noEndBorder, noTopBorder }) {
  return (
    <Grid
      xs={xs}
      item
      sx={{
        flexGrow: 1,
      }}
    >
      <Card
        variant="outlined"
        elevation={2}
        sx={{
          p: 0,
          width: "100%",
          height: "100% !important",
          borderLeft: "none",
          borderTop: noTopBorder ? "none" : undefined,
          borderBottom: "none",
          borderRadius: "0",
          borderRight: noEndBorder ? "none" : undefined,
        }}
      >
        <CardHeader
          title={
            typeof title == "string" ? (
              <Typography fontSize="medium" fontWeight="500">
                {title}
              </Typography>
            ) : (
              title
            )
          }
          sx={{ px: 2, pt: 1.5, pb: 1, m: 0 }}
        />
        <CardContent
          sx={{
            px: 2,
            py: 0,
            pb: 0,
            m: 0,
            paddingBottom: "16px !important",
            minHeight: "0px",
            height: "100%",
          }}
        >
          {content}
        </CardContent>
      </Card>
    </Grid>
  );
}

// function formatDateTime(inputDate) {
//   const now = new Date();
//   const date = new Date(inputDate); // ensure it's a Date object

//   // Strip time for date-only comparison
//   const startOfToday = new Date(
//     now.getFullYear(),
//     now.getMonth(),
//     now.getDate()
//   );
//   const startOfDate = new Date(
//     date.getFullYear(),
//     date.getMonth(),
//     date.getDate()
//   );

//   const msInDay = 86400000;
//   const dayDiff = Math.round((startOfDate - startOfToday) / msInDay);

//   const timeOptions = {
//     hour: "numeric",
//     minute: "2-digit",
//     hour12: true,
//   };
//   const formattedTime = date
//     .toLocaleTimeString("en-US", timeOptions)
//     .toLowerCase()
//     .replace(":00", "");

//   if (dayDiff === -1) {
//     return `Yesterday, at ${formattedTime}`;
//   } else if (dayDiff === 0) {
//     return `Today, at ${formattedTime}`;
//   } else if (dayDiff === 1) {
//     return `Tomorrow, ${formattedTime}`;
//   } else {
//     const fullDate = date.toLocaleDateString("en-US", {
//       weekday: "long",
//       day: "numeric",
//       month: "long",
//       year: "numeric",
//     });
//     return `${fullDate}, ${formattedTime}`;
//   }
// }

// function TaskElement({
//   task,
//   setEditDialogIsOpen,
//   setCurrentTask,
//   options,
//   setTasks,
// }) {
//   const [open, setOpen] = useState({});
//   const [updatedTask, setUpdatedTask] = useState({});

//   async function updateTask() {
//     const [response, data] = await tasksService.updateTask(
//       task.id,
//       updatedTask
//     );
//     if (response.ok) {
//       setUpdatedTask({});
//       setTasks(data);
//     }
//   }
//   useEffect(() => {
//     if (Object.keys(updatedTask).length) {
//       updateTask();
//     }
//   }, [updatedTask]);

//   if (!task) return;
//   return (
//     <Grid item xs={6}>
//       <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
//         <Box
//           sx={{
//             width: "100%",
//             display: "flex",
//             flexDirection: "row",
//             justifyContent: "space-between",
//             gap: 1,
//           }}
//         >
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "row",
//               alignItems: "center",
//               justifyContent: "space-between",
//               gap: 1,
//             }}
//           >
//             <Typography
//               variant="body2"
//               sx={{ fontWeight: "500", fontSize: "0.9rem" }}
//             >
//               {formatDateTime(task.dueDate)}
//             </Typography>
//             {new Date(task.dueDate) < new Date() ? (
//               <Chip label={"Overdue"} size="small" color="error" />
//             ) : null}
//           </Box>
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: "row",
//               alignItems: "center",
//               justifyContent: "space-between",
//               gap: 1,
//             }}
//           >
//             <Chip
//               label={task.priority.name}
//               sx={{ color: "white !important" }}
//               size="small"
//               color={
//                 TASK_PRIORITY_COLORS[
//                   updatedTask.priorityId || task.priority?.id || 0
//                 ]
//               }
//             />
//             <Chip
//               label={
//                 updatedTask.statusId
//                   ? options.statuses.find(
//                       (status) => status.id == updatedTask.statusId
//                     )?.label
//                   : task.status.name
//               }
//               color={
//                 TASK_STATUS_COLORS[updatedTask.statusId || task.status?.id || 0]
//               }
//               size="small"
//               onDelete={(e) => {
//                 setOpen({
//                   ...open,
//                   statusMenu: {
//                     anchorElement: e.currentTarget,
//                     open: true,
//                   },
//                 });
//               }}
//               sx={{ color: "white !important" }}
//               onClick={(e) => {
//                 setOpen({
//                   ...open,
//                   statusMenu: {
//                     anchorElement: e.currentTarget,
//                     open: true,
//                   },
//                 });
//               }}
//               deleteIcon={<ArrowDropDown sx={{ color: "white !important" }} />}
//               clickable
//             />
//             <Menu
//               open={!!open.statusMenu?.open}
//               anchorEl={open.statusMenu?.anchorElement}
//               onClose={() => {
//                 setOpen({
//                   ...open,
//                   statusMenu: {
//                     ...open.statusMenu,
//                     open: false,
//                   },
//                 });
//               }}
//             >
//               {options.statuses.map((status) => {
//                 return (
//                   <MenuItem
//                     onClick={() => {
//                       setOpen({
//                         ...open,
//                         statusMenu: {
//                           ...open.statusMenu,
//                           open: false,
//                         },
//                       });
//                       setUpdatedTask({ ...updatedTask, statusId: status.id });
//                     }}
//                     selected={
//                       (updatedTask.statusId || task.statusId) == status.id
//                     }
//                     key={"Status: " + status.id}
//                     sx={{ borderRadius: "6px", px: 1.5, py: 0.75 }}
//                   >
//                     {status.label}
//                   </MenuItem>
//                 );
//               })}
//             </Menu>
//             <IconButton
//               size="small"
//               onClick={() => {
//                 setEditDialogIsOpen(Math.random());
//                 setCurrentTask(task);
//               }}
//             >
//               <Edit />
//             </IconButton>
//           </Box>
//         </Box>

//         <Typography
//           sx={{
//             fontSize: "1rem",
//             lineHeight: "22px",
//             mt: 1,
//             flex: 1,
//           }}
//         >
//           {task.actionToTake}
//         </Typography>

//         <Box
//           sx={(theme) => ({
//             display: "flex",
//             flexDirection: "column",
//             borderTop: "1px solid " + theme.palette.divider,
//             mt: 2,
//             mx: -2,
//           })}
//         >
//           {[
//             {
//               label: "Remarks",
//               value: true ? (
//                 <ConvertibleInput
//                   readOnly={false}
//                   value={task.remarks}
//                   onSave={(newRemarks) => {
//                     setUpdatedTask({ ...updatedTask, remarks: newRemarks });
//                   }}
//                   waitForSave={true}
//                   onCancel={() => {}}
//                   onChange={() => {}}
//                 />
//               ) : (
//                 task.remarks
//               ),
//             },
//             { label: "Assigned to", value: task.assignedTo?.name },
//             {
//               label: "KPI",
//               value: task.auditKpi?.description,
//             },
//             {
//               label: "Audit",
//               value: task.auditKpi
//                 ? task.auditKpi?.audit?.branch.branchGroup.name +
//                   " - " +
//                   task.auditKpi?.audit?.branch.name +
//                   " - " +
//                   task?.auditKpi?.audit?.description
//                 : null,
//             },
//             {
//               label: "",
//               value: (
//                 <Attachments
//                   attachments={updatedTask.attachments || task.attachments}
//                   onRemoveAttachment={(_, index) => {
//                     const newAttachments = updatedTask.attachments
//                       ? [...updatedTask.attachments]
//                       : [...task.attachments];
//                     newAttachments.splice(index, 1);
//                     setUpdatedTask({
//                       ...updatedTask,
//                       attachments: newAttachments,
//                     });
//                   }}
//                   onAddAttachments={(files) => {
//                     setUpdatedTask({
//                       ...updatedTask,
//                       attachments: [
//                         ...(updatedTask.attachments || task.attachments),
//                         ...files.map((url) => ({ url })),
//                       ],
//                     });
//                   }}
//                 />
//               ),
//               noBottomBorder: true,
//             },
//           ].map((element) => {
//             return (
//               <Box
//                 sx={(theme) => ({
//                   display: "flex",
//                   flexDirection: "row",
//                   justifyContent: "space-between",
//                   alignItems: "center",
//                   px: 2,
//                   py: 0.5,
//                   gap: 2,
//                   borderBottom: element.noBottomBorder
//                     ? ""
//                     : "1px solid " + theme.palette.divider,
//                 })}
//               >
//                 {element.label ? (
//                   <Typography sx={{ fontSize: "0.9rem", fontWeight: "400" }}>
//                     {element.label}
//                   </Typography>
//                 ) : null}
//                 <Typography
//                   sx={{
//                     fontSize: "0.9rem",
//                     fontWeight: element.value ? "500" : "400",
//                     fontStyle: element.value ? null : "italic",
//                     textAlign: "end",
//                     whiteSpace: "wrap",
//                   }}
//                   color={element.value ? null : "GrayText"}
//                 >
//                   {element.value || "None"}
//                 </Typography>
//               </Box>
//             );
//           })}
//         </Box>
//       </Card>
//     </Grid>
//   );
// }
